import { Paper } from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import { Spinner, Table } from "react-bootstrap";
import { useAlert } from "react-alert";
import { useSelector } from "react-redux";
import ReactToPrint from "react-to-print";
import Select from "react-select";
import { upperCase } from "lodash";
import { baseUrl } from "../../../../services/config";

const ExamAnalysisReport = () => {
  const alert = useAlert();
  const authtoken = localStorage.getItem("userToken");
  const user = useSelector((state) => state.user.user);

  const [sessionData, setSessionData] = useState([]);
  const [yearId, setYearId] = useState("");
  const [campusData] = useState(user?.campuses || []);
  const [campusId, setCampusId] = useState("");

  const [availableExams, setAvailableExams] = useState([]);
  const [examsLoading, setExamsLoading] = useState(false);
  const [selectedExams, setSelectedExams] = useState([]);

  const [availableClasses, setAvailableClasses] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const [unitName, setUnitName] = useState("");
  const [icon, setIcon] = useState([]);

  const [printloading, setprintloading] = React.useState(false);
  const [text, setText] = React.useState("old boring text");
  const componentRef = useRef(null);
  const onBeforeGetContentResolve = React.useRef(null);

  useEffect(() => {
    getSettingData();
    getSessions();
  }, []);

  const getSettingData = async () => {
    await fetch(`${baseUrl}/get-settings`, {
      method: "GET",
      headers: { Authorization: `Bearer ${authtoken}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setUnitName(upperCase(data.payload.settings.unit_name));
        setIcon(data.payload.settings.logo_file);
      })
      .catch(console.log);
  };

  const getSessions = () => {
    fetch(`${baseUrl}/session`, {
      headers: { Authorization: `Bearer ${authtoken}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setSessionData(data.payload);
        data.payload.forEach((s) => {
          if (s.active_academic_year == "1") setYearId(s.id);
        });
      })
      .catch(console.log);
  };

  const loadExams = (sessionId, campus) => {
    if (!sessionId || !campus) return;
    setAvailableExams([]);
    setSelectedExams([]);
    setAvailableClasses([]);
    setSelectedClasses([]);
    setIsVisible(false);
    setReportData(null);
    setExamsLoading(true);
    fetch(`${baseUrl}/exam_for_session`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${authtoken}`,
      },
      body: JSON.stringify({ year_id: sessionId, campus_id: campus }),
    })
      .then((res) => res.json())
      .then((data) => {
        setExamsLoading(false);
        const payload = data.payload;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.exams)
          ? payload.exams
          : [];
        setAvailableExams(list);
      })
      .catch(() => setExamsLoading(false));
  };

  const handleSessionChange = (e) => {
    const val = e.target.value;
    setYearId(val);
    loadExams(val, campusId);
  };

  const handleCampusChange = (e) => {
    const val = e.target.value;
    setCampusId(val);
    loadExams(yearId, val);
  };

  const handleExamsChange = (selected) => {
    const list = selected || [];
    if (list.length > 5) {
      alert.error("Maximum 5 exams can be selected");
      return;
    }
    setSelectedExams(list);
    setIsVisible(false);
    setReportData(null);

    // Rebuild available classes as the union of student_classes from all selected exams
    const classMap = {};
    list.forEach((ex) => {
      (ex.student_classes || []).forEach((c) => {
        classMap[c.id] = c;
      });
    });
    const merged = Object.values(classMap);
    setAvailableClasses(merged);
    // Remove any selected classes that are no longer in the union
    setSelectedClasses((prev) =>
      prev.filter((sc) => classMap[sc.value] !== undefined)
    );
  };

  const handleGetData = () => {
    if (!yearId) { alert.error("Please select a session"); return; }
    if (!campusId) { alert.error("Please select a campus"); return; }
    if (selectedExams.length < 2) { alert.error("Please select at least 2 exams"); return; }

    setLoading(true);
    setIsVisible(false);
    setReportData(null);

    fetch(`${baseUrl}/exam-analysis-report`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${authtoken}`,
      },
      body: JSON.stringify({
        session_id: yearId,
        campus_id: campusId,
        exam_ids: selectedExams.map((e) => e.value),
        student_class_ids:
          selectedClasses.length > 0
            ? selectedClasses.map((c) => c.value)
            : availableClasses.map((c) => c.id),
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        setLoading(false);
        setIsVisible(true);
        setReportData(data.payload || null);
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  const handleBeforePrint = React.useCallback(() => {
    console.log("`onBeforePrint` called");
  }, []);

  const handleOnBeforeGetContent = React.useCallback(() => {
    console.log("`onBeforeGetContent` called");
    setprintloading(true);
    setText("Loading new text...");
    return new Promise((resolve) => {
      onBeforeGetContentResolve.current = resolve;
      setTimeout(() => {
        setprintloading(false);
        setText("New, Updated Text!");
        resolve();
      }, 2000);
    });
  }, [setprintloading, setText]);

  const handleAfterPrint = React.useCallback(() => {
    console.log("`onAfterPrint` called");
  }, []);

  const examOptions = availableExams.map((ex) => ({
    value: ex.id,
    label: String(ex.exam_name ?? ""),
    student_classes: ex.student_classes || [],
  }));

  const classOptions = availableClasses.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  const selectedCampusName = campusData.find((c) => c.id == campusId)?.name || "";
  const selectedSession = sessionData.find((s) => s.id == yearId)?.year || "";

  const exams = reportData?.exams || [];
  const isComparison = exams.length >= 2;
  const classes = reportData?.classes || [];

  const pctColor = (pct) => {
    if (pct === null || pct === undefined) return "#aaa";
    if (pct >= 80) return "#1BC5BD";
    if (pct >= 50) return "#FFA800";
    return "#F64E60";
  };

  const ReportContent = () => (
    <div id="tableboot">
      {/* header */}
      <div className="p-3" style={{ display: "flex", alignItems: "center", gap: 15 }}>
        <img style={{ height: 80, width: 80 }} src={icon} alt="logo" />
        <div>
          <h2 style={{ margin: 0 }}>{unitName}</h2>
          <h5 style={{ color: "#7E8299", margin: 0 }}>Exam Analysis Report</h5>
        </div>
      </div>

      {/* filter summary */}
      <div className="px-3 pb-2" style={{ fontSize: 13 }}>
        <b>Session:</b> {selectedSession}&nbsp;&nbsp;
        <b>Campus:</b> {selectedCampusName}&nbsp;&nbsp;
        {exams.length > 0 && (
          <><b>Exams:</b> {exams.map((e) => e.name).join(", ")}&nbsp;&nbsp;</>
        )}
      </div>

      {/* table */}
      <Table bordered style={{ fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#F3F6F9" }}>
            <th>Subject</th>
            {exams.map((ex) => (
              <th key={ex.id} style={{ textAlign: "center", minWidth: 110 }}>
                {ex.name}
                <div style={{ fontWeight: 400, fontSize: 11, color: "#7E8299" }}>Avg %</div>
              </th>
            ))}
            {isComparison && (
              <th style={{ textAlign: "center", minWidth: 90 }}>Variance</th>
            )}
          </tr>
        </thead>
        <tbody>
          {classes.length > 0 ? (
            classes.map((cls) => (
              <React.Fragment key={cls.class_id}>
                {/* class row */}
                <tr style={{ background: "#EEF0F8" }}>
                  <td
                    colSpan={exams.length + (isComparison ? 2 : 1)}
                    style={{ color: "#181C32", fontWeight: 700, fontSize: 15, paddingLeft: 8 }}
                  >
                    {cls.class_name}
                  </td>
                </tr>

                {(cls.sections || []).map((sec) => (
                  <React.Fragment key={sec.section_id}>
                    {/* section row */}
                    <tr style={{ background: "#F8F9FA" }}>
                      <td
                        colSpan={exams.length + (isComparison ? 2 : 1)}
                        style={{ color: "#3F4254", fontWeight: 700, paddingLeft: 20 }}
                      >
                        &nbsp;{cls.class_name}-{sec.section_name}
                      </td>
                    </tr>

                    {(sec.subjects || []).map((sub) => (
                      <tr key={sub.subject_id}>
                        <td style={{ color: "#3F4254", paddingLeft: 36 }}>
                          &nbsp;&nbsp;{sub.subject_name}
                        </td>
                        {exams.map((ex) => {
                          const d = sub.exams?.[ex.id];
                          const pct = d?.avg_percent ?? null;
                          return (
                            <td key={ex.id} style={{ textAlign: "center" }}>
                              {pct !== null ? (
                                <>
                                  <div style={{ color: pctColor(pct), fontWeight: 600 }}>
                                    {pct}%
                                  </div>
                                  <div style={{ color: "#7E8299", fontSize: 11 }}>
                                    {d.total_students} students
                                  </div>
                                </>
                              ) : (
                                <span style={{ color: "#ccc" }}>—</span>
                              )}
                            </td>
                          );
                        })}
                        {isComparison && (
                          <td style={{ textAlign: "center" }}>
                            {sub.variance !== null && sub.variance !== undefined ? (
                              <span
                                style={{
                                  color: sub.variance >= 0 ? "#1BC5BD" : "#F64E60",
                                  fontWeight: 600,
                                }}
                              >
                                {sub.variance >= 0 ? "+" : ""}{sub.variance}%
                              </span>
                            ) : (
                              <span style={{ color: "#ccc" }}>—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))
          ) : (
            <tr>
              <td
                colSpan={exams.length + (isComparison ? 2 : 1)}
                style={{ textAlign: "center" }}
              >
                No data found
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );

  return (
    <Paper style={{ marginTop: 10, marginBottom: 10, padding: 15 }}>
      {/* row 1 */}
      <div className="row">
        <div className="col-md-3">
          <label><b>Select Session</b> <span style={{ color: "red", fontSize: 12 }}>*</span></label>
          <select style={{ height: 40 }} className="form-select" value={yearId} onChange={handleSessionChange}>
            <option value="">Select Session</option>
            {sessionData.map((s) => (
              <option key={s.id} value={s.id}>{s.year}</option>
            ))}
          </select>
        </div>

        <div className="col-md-3">
          <label><b>Select Campus</b> <span style={{ color: "red", fontSize: 12 }}>*</span></label>
          <select style={{ height: 40 }} className="form-select" value={campusId} onChange={handleCampusChange}>
            <option value="">Select Campus</option>
            {campusData.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="col-md-3">
          <label>
            <b>Select Exams</b> <span style={{ color: "red", fontSize: 12 }}>*</span>
            {examsLoading && <Spinner animation="border" size="sm" variant="info" className="ml-2" />}
          </label>
          <Select
            isMulti
            options={examOptions}
            value={selectedExams}
            onChange={handleExamsChange}
            placeholder={availableExams.length === 0 ? "Select Session & Campus first" : "Select exams (max 5)"}
            isDisabled={availableExams.length === 0}
          />
        </div>

        <div className="col-md-3">
          <label><b>Select Classes</b> <span style={{ color: "#999", fontSize: 12 }}>(optional — leave empty for all)</span></label>
          <Select
            isMulti
            options={classOptions}
            value={selectedClasses}
            onChange={(sel) => {
              setSelectedClasses(sel || []);
              setIsVisible(false);
              setReportData(null);
            }}
            placeholder={availableClasses.length === 0 ? "Select exams first" : "Select classes"}
            isDisabled={availableClasses.length === 0}
          />
        </div>
      </div>

      {/* row 2 — buttons */}
      <div className="row mt-3">
        <div className="col-md-12 d-flex align-items-center" style={{ gap: 8 }}>
          <button className="btn btn-primary" style={{ height: 40 }} onClick={handleGetData}>
            Show
          </button>
          {isVisible && reportData && (
            <ReactToPrint
              documentTitle="Exam Analysis Report"
              pageStyle="auto"
              onAfterPrint={handleAfterPrint}
              onBeforeGetContent={handleOnBeforeGetContent}
              onBeforePrint={handleBeforePrint}
              removeAfterPrint={true}
              trigger={() => (
                <button
                  disabled={printloading ? true : false}
                  className="btn btn-primary"
                  style={{ height: 40 }}
                >
                  {printloading ? <span>Printing..</span> : "Print"}
                </button>
              )}
              content={() => componentRef.current}
            />
          )}
        </div>
      </div>

      {/* on-screen result */}
      <div className="mt-5">
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Spinner class="text-center" variant="info" animation="border" />
          </div>
        ) : (
          isVisible && reportData && <ReportContent />
        )}
      </div>

      {/* always-mounted hidden print section */}
      <div
        ref={componentRef}
        id="printSection"
        style={{ visibility: "hidden" }}
        className="tablePrint"
      >
        <ReportContent />
      </div>
    </Paper>
  );
};

export default ExamAnalysisReport;
